import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CurecallDocumentViewer, LockIcon } from '@honestica/curecall-ui'
import { EPermissionSpecialAuthorization } from 'src/pages/configuration/permissions/_components/permissions.type'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { usePatientVariableDocument } from './use-patient-variable-document.hook'
import { PDF_WORKER_SRC } from 'src/utils/pdf-worker.util'
import { isNotFoundError } from 'src/utils/http-error.util'
import { MissingDocumentFileBadge } from 'src/components/patient/missing-document-file-badge.component'

interface PatientVariableDocumentProps {
  variableId: number
  patientFormId: number
}

const PatientVariableDocument = ({ variableId, patientFormId }: PatientVariableDocumentProps) => {
  const { patientId, projectId } = useParams()
  const { t } = useTranslation()
  const { hasSpecialAuthorization } = useCurrentProject()
  const canViewSensitiveData = hasSpecialAuthorization(EPermissionSpecialAuthorization.CAN_VIEW_SENSITIVE_DATA)

  const { isLoading, isError, error, objectUrl, mimeType, refetch } = usePatientVariableDocument(
    variableId,
    patientFormId,
    patientId as string,
    projectId as string,
    canViewSensitiveData,
  )

  if (!canViewSensitiveData) {
    return (
      <span className="c-flex c-items-center c-gap-1.5 c-text-sm c-text-grey-600">
        <LockIcon className="c-size-3.5 c-shrink-0" />
        {t('components.sms.image-restricted-access')}
      </span>
    )
  }

  if (isError && isNotFoundError(error)) {
    return <MissingDocumentFileBadge />
  }

  if (!isLoading && !isError && !objectUrl) {
    return null
  }

  return (
    <CurecallDocumentViewer
      objectUrl={objectUrl ?? ''}
      mimeType={mimeType}
      pdfWorkerSrc={PDF_WORKER_SRC}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      variant="inline"
      openLabel={t('components.patient-variable-document.visualize')}
      lightboxLabels={{
        closeLabel: t('common.actions.close'),
        zoomInLabel: t('components.image-lightbox.zoom-in'),
        zoomOutLabel: t('components.image-lightbox.zoom-out'),
        zoomTo100Label: t('components.image-lightbox.zoom-to-100'),
        resetViewLabel: t('components.image-lightbox.reset-view'),
        imageViewerLabel: t('components.image-lightbox.image-viewer'),
        downloadLabel: t('common.actions.download'),
      }}
      pdfLabels={{
        pageLabel: (current, total) => t('components.pdf-viewer.page-label', { current, total }),
        previousPageLabel: t('components.pdf-viewer.previous-page'),
        nextPageLabel: t('components.pdf-viewer.next-page'),
        zoomInLabel: t('components.image-lightbox.zoom-in'),
        zoomOutLabel: t('components.image-lightbox.zoom-out'),
        downloadLabel: t('common.actions.download'),
        loadErrorLabel: t('components.pdf-viewer.load-error'),
        retryLabel: t('components.sms.image-retry'),
        loadingLabel: t('components.pdf-viewer.loading'),
        zoomTo100Label: t('components.image-lightbox.zoom-to-100'),
        resetScaleLabel: t('components.image-lightbox.reset-view'),
      }}
    />
  )
}

export default PatientVariableDocument
